import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Input, Label, Row, Col } from 'reactstrap';
import axios from 'axios';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import notify from '../../../../../../../utils/notify';
import {
  confirmAction,
  confirmDelete,
} from '../../../../../../../utils/confirm';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import { api } from '../../../../../../../config';
import { formatForFlatpickr } from '../../../../../../../utils/dateformatpickr';
import {
  DetailItem,
  SectionHeader,
  SectionCard,
  RespondNotesEditor,
} from '../../../../../../../shared/offer-domain/ui';
import CounterNegotiationCard from '../../../../../../../shared/offer-domain/counter/CounterNegotiationCard';
import {
  buildCounterPayload,
  COUNTER_API_FIELD_MAP,
  formatDateTime,
  formatOperationalTolerance,
  getCounterDraftValue,
  getOperationalToleranceOptionsCounter,
  getOtValidityAgreeable,
  hasOperationalToleranceCounter,
  hasProposalValidityCounter,
  isValidDateValue,
  isValidOperationalToleranceValue,
  OPERATIONAL_TOLERANCE_OPTIONS,
  parseOperationalToleranceValue,
  resolveOperationalToleranceOption,
  withBidderCounter,
} from '../../../../../../../shared/offer-domain/counter';
import ClauseDetailSection from '../components/ClauseDetailSection';
import IcRespondAnchor from '../components/IcRespondAnchor';
import ClauseCard from '../../../../../../../shared/offer-domain/clause';
import PaymentComplianceView from '../components/PaymentComplianceView';
import {
  PAYMENT_COMPLIANCE_FIELDS,
  PAYMENT_TERM_FIELDS,
  isFreeTextField,
  isGroupAgreeField,
  getAgreedFinal,
  getFreeTextAgreedFinal,
  isFieldAgreeable,
  isFieldCountered,
  hasAnyFieldCountered,
  isCounterGroupAgreed,
  pickLatestCounter,
} from '../../../../../../../shared/offer-domain/payment';
import AgreeLockButton from '../../../../../../../shared/offer-domain/clause/AgreeLockButton';

// Thread per field hanya untuk free-text (11/14/15, diskusi tanpa lock);
// agree per field = baris agreement (8-13 pilihan, 19-21 free-text).
const THREAD_FIELDS = PAYMENT_COMPLIANCE_FIELDS.filter(isFreeTextField);
const AGREE_FIELDS = PAYMENT_COMPLIANCE_FIELDS.filter(isGroupAgreeField);
import { PortRestrictionsTable } from '../../../../../../../Components/PortRestriction';
import {
  getProcurementProductNameId,
  getProcurementProductTypeNames,
  hasMultipleProductTypes,
} from '../../../../../../../shared/offer-domain/constants';
import {
  displayValue,
  formatProposalDeadline,
} from '../../../../../utils/tenderEvaluationHelpers';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  needsIcReplyOnEvaluatorThread,
  getLastOwnEditableResponse,
  isLastResponseEvaluator,
  needsTeAgreeOnLastCounter,
  hasTeClarificationRespondReady,
  toToleranceValidityHistoryItems,
  TYPE_RESPONSE_TOLERANCE,
  TYPE_RESPONSE_VALIDITY,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  TYPE_RESPONSE_PRODUCT_SPECIFICATION,
  TYPE_RESPONSE_OT_VALUE,
  TYPE_RESPONSE_OT_OPTIONS,
  TYPE_RESPONSE_VALIDITY_VALID_TO,
  createLockRespondHandler,
} from '../../../../../../../shared/offer-domain/respond';
import useClarificationToleranceValidityResponses from '../../../../../../../shared/offer-domain/respond/useClarificationToleranceValidityResponses';
import useSubmittedBidder from '../../../../../hooks/useSubmittedBidder';

const FIELD_TYPE_MAP = {
  operationalTolerance: TYPE_RESPONSE_TOLERANCE,
  proposalValidityPeriodMax: TYPE_RESPONSE_VALIDITY,
  globalNotes: TYPE_RESPONSE_GLOBAL,
  productSpecification: TYPE_RESPONSE_PRODUCT_SPECIFICATION,
  // Thread kelompok: Invoicing/Mode/Currency (5), Law/Arbitration (6).
  counterPaymentTerm: TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  counterCompliance: TYPE_RESPONSE_COUNTER_COMPLIANCE,
  ...Object.fromEntries(THREAD_FIELDS.map(def => [def.key, def.typeResponse])),
};

const EVALUATOR_FORM_MAIN = {
  statusOfferingWordingFlag: false,
};

const DEFAULT_AGREE_NOTICE =
  'If you do not respond, the status is considered Agree. You can still respond later if you disagree.';

const PROPOSAL_VALIDITY_FLATPICKR_OPTIONS = {
  enableTime: true,
  dateFormat: 'd/m/Y H:i',
  time_24hr: true,
};

const DefaultAgreeNoticeAlert = () => (
  <Alert
    color='info'
    className='d-flex align-items-start gap-2 border-0 bg-light text-primary mb-3'
  >
    <i className='ri-information-line flex-shrink-0 mt-1'></i>
    <span className='small'>{DEFAULT_AGREE_NOTICE}</span>
  </Alert>
);

const OfferingTab = ({
  procurement: procurementProp,
  procurementId,
  offerId,
  procurementMitraId: procurementMitraIdProp = null,
  isEvaluatorView = true,
  isRespondClosed: isRespondClosedProp = null,
  highlightArmed = false,
  onPendingRespondIds,
  onRefreshProcurement = null,
}) => {
  const token = getAuthToken();
  const { items, refresh: refreshSubmittedBidders } =
    useSubmittedBidder(procurementId);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editingCounterField, setEditingCounterField] = useState(null);
  const [counterDraft, setCounterDraft] = useState({});
  const [isSavingCounter, setIsSavingCounter] = useState(false);
  const [draftNotes, setDraftNotes] = useState({});

  const matchedBidder = useMemo(
    () =>
      items.find(
        item =>
          String(item.idOffer) === String(offerId) ||
          String(item.id) === String(offerId) ||
          (procurementMitraIdProp &&
            String(item.id) === String(procurementMitraIdProp))
      ) ?? null,
    [items, offerId, procurementMitraIdProp]
  );

  // Counter OT/Validity per mitra (bukan root procurement).
  const procurement = useMemo(
    () => withBidderCounter(procurementProp, matchedBidder),
    [procurementProp, matchedBidder]
  );

  const bidder = useMemo(() => {
    if (procurementMitraIdProp) {
      return {
        ...matchedBidder,
        id: procurementMitraIdProp,
        idOffer: matchedBidder?.idOffer,
      };
    }
    return matchedBidder;
  }, [matchedBidder, procurementMitraIdProp]);

  useEffect(() => {
    setExpandedSections({});
    setEditingField(null);
    setEditingCounterField(null);
    setCounterDraft({});
    setDraftNotes({});
  }, [offerId, bidder?.id]);

  const isRespondClosed = Boolean(isRespondClosedProp);

  const {
    responses,
    isLoading: isResponsesLoading,
    isSaving,
    saveResponse,
    deleteResponse,
    lockResponse,
    agreeField,
  } = useClarificationToleranceValidityResponses(
    token,
    procurementId,
    bidder?.id,
    isEvaluatorView
  );

  const handleLockRespond = createLockRespondHandler(lockResponse);

  const fieldAgreements = useMemo(
    () =>
      Object.fromEntries(
        PAYMENT_COMPLIANCE_FIELDS.map(def => [
          def.agreeTypeResponse,
          responses[def.agreeTypeResponse],
        ])
      ),
    [responses]
  );

  const handleAgreeField = async (typeResponse, isLocked, finalValue) => {
    try {
      await agreeField(typeResponse, isLocked, finalValue);
      notify.success(isLocked ? 'Field agreed and locked.' : 'Field unlocked.');
    } catch (error) {
      notify.apiError(error, isLocked ? 'agree the field' : 'unlock the field');
      return false;
    }
    return true;
  };

  // OT/Validity: Agree & Lock per field (16/17/18), bukan per thread 1/2.
  const otValidityAgreeable = getOtValidityAgreeable(procurement);
  const agreedOtValue = getAgreedFinal(responses[TYPE_RESPONSE_OT_VALUE]);
  const agreedOtOptions = getAgreedFinal(responses[TYPE_RESPONSE_OT_OPTIONS]);
  const agreedValidity = getAgreedFinal(
    responses[TYPE_RESPONSE_VALIDITY_VALID_TO]
  );

  // Free-text: agreement 19-21, atau lock lama thread 11/14/15.
  const freeTextAgreed = useMemo(
    () =>
      Object.fromEntries(
        THREAD_FIELDS.map(def => [
          def.key,
          Boolean(getFreeTextAgreedFinal(def, responses)),
        ])
      ),
    [responses]
  );

  // Thread kelompok (5/6/1/2) dianggap selesai bila semua field yang dicounter sudah di-agree.
  const groupAgreed = (defs, latest) =>
    isCounterGroupAgreed(
      defs.map(def => ({
        agreed: Boolean(getAgreedFinal(fieldAgreements[def.agreeTypeResponse])),
        agreeable: isFieldAgreeable(def, latest, procurement),
      }))
    );
  const latestPaymentCounter = pickLatestCounter(
    procurement?.counterPaymentTerm,
    bidder?.id
  );
  const latestComplianceCounter = pickLatestCounter(
    procurement?.counterCompliance,
    bidder?.id
  );
  const paymentChoiceDefs = AGREE_FIELDS.filter(def =>
    [8, 9, 10].includes(def.typeResponse)
  );
  const complianceChoiceDefs = AGREE_FIELDS.filter(def =>
    [12, 13].includes(def.typeResponse)
  );
  const hasPaymentChoiceCounter = hasAnyFieldCountered(
    paymentChoiceDefs,
    latestPaymentCounter,
    procurement
  );
  const hasComplianceChoiceCounter = hasAnyFieldCountered(
    complianceChoiceDefs,
    latestComplianceCounter,
    procurement
  );
  const latestCounterForField = def =>
    PAYMENT_TERM_FIELDS.some(item => item.key === def.key)
      ? latestPaymentCounter
      : latestComplianceCounter;
  const resolvedGroups = {
    counterPaymentTerm: groupAgreed(paymentChoiceDefs, latestPaymentCounter),
    counterCompliance: groupAgreed(
      complianceChoiceDefs,
      latestComplianceCounter
    ),
    proposalValidityPeriodMax: isCounterGroupAgreed([
      {
        agreed: Boolean(agreedValidity),
        agreeable: otValidityAgreeable.validity,
      },
    ]),
    operationalTolerance: isCounterGroupAgreed([
      {
        agreed: Boolean(agreedOtValue),
        agreeable: otValidityAgreeable.otValue,
      },
      {
        agreed: Boolean(agreedOtOptions),
        agreeable: otValidityAgreeable.otOptions,
      },
    ]),
    ...freeTextAgreed,
  };

  const renderFieldAgree = (typeResponse, label, agreedFinal, agreeable) => {
    const canAgree =
      isEvaluatorView && !isRespondClosed && Boolean(agreedFinal || agreeable);
    if (!canAgree && !agreedFinal) return null;
    return (
      <Col lg={6} key={typeResponse}>
        <div className='text-muted small fw-semibold mb-1'>{label}</div>
        <AgreeLockButton
          isLocked={Boolean(agreedFinal)}
          onLock={
            canAgree
              ? locked => handleAgreeField(typeResponse, locked)
              : undefined
          }
          disabled={isSaving}
        />
        {agreedFinal && (
          <div className='small text-primary fw-semibold mt-1'>
            Agreed · Final: {agreedFinal}
          </div>
        )}
      </Col>
    );
  };

  const renderFieldAgreeRow = items => {
    const cols = items.map(args => renderFieldAgree(...args)).filter(Boolean);
    return cols.length ? <Row className='gy-3 mt-1 mb-2'>{cols}</Row> : null;
  };

  const productName = procurement?.productType?.product?.name;
  const productType = hasMultipleProductTypes(procurement)
    ? getProcurementProductTypeNames(procurement)
    : procurement?.productType?.description;
  const productSpecification = procurement?.productSpecification;

  const hasToleranceCounter = hasOperationalToleranceCounter(
    procurement?.operationalToleranceCounter,
    procurement?.operationalToleranceOptionsCounter,
    procurement?.operationalToleranceOptionsCounterLabel
  );
  const hasValidityCounter = hasProposalValidityCounter(
    procurement?.proposalValidityPeriodMaxCounter
  );

  const toleranceResponses = useMemo(
    () => responses[TYPE_RESPONSE_TOLERANCE] ?? [],
    [responses]
  );
  const validityResponses = useMemo(
    () => responses[TYPE_RESPONSE_VALIDITY] ?? [],
    [responses]
  );

  const toleranceOwnResponse = useMemo(
    () =>
      getOwnClauseResponse(toleranceResponses, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, toleranceResponses]
  );
  const validityOwnResponse = useMemo(
    () =>
      getOwnClauseResponse(validityResponses, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, validityResponses]
  );

  const toleranceHistory = useMemo(
    () => toToleranceValidityHistoryItems(toleranceResponses),
    [toleranceResponses]
  );
  const validityHistory = useMemo(
    () => toToleranceValidityHistoryItems(validityResponses),
    [validityResponses]
  );

  const canReplyTolerance = isEvaluatorView
    ? !isRespondClosed &&
      !resolvedGroups.operationalTolerance &&
      !isLastResponseEvaluator(toleranceResponses)
    : needsIcReplyOnEvaluatorThread(toleranceResponses);
  const canReplyValidity = isEvaluatorView
    ? !isRespondClosed &&
      !resolvedGroups.proposalValidityPeriodMax &&
      !isLastResponseEvaluator(validityResponses)
    : needsIcReplyOnEvaluatorThread(validityResponses);

  // IC: setelah Evaluator respond, mitra boleh edit ulang counter (pola Offer).
  const allowValidityCounterEdit =
    !isEvaluatorView && !isRespondClosed && canReplyValidity;
  const allowToleranceCounterEdit =
    !isEvaluatorView && !isRespondClosed && canReplyTolerance;

  const refreshAfterCounterSave = useCallback(async () => {
    if (typeof onRefreshProcurement === 'function') {
      await onRefreshProcurement();
    }
    if (typeof refreshSubmittedBidders === 'function') {
      await refreshSubmittedBidders();
    }
  }, [onRefreshProcurement, refreshSubmittedBidders]);

  const startCounterEdit = field => {
    setEditingCounterField(field);
    setCounterDraft(prev => ({
      ...prev,
      ...getCounterDraftValue({
        field,
        form: procurement,
        savedCounters: {
          operationalTolerance: procurement?.operationalToleranceCounter,
          operationalToleranceOption: resolveOperationalToleranceOption({
            label: procurement?.operationalToleranceOptionsCounterLabel,
            optionsCounter: procurement?.operationalToleranceOptionsCounter,
          }),
          proposalValidityPeriodMax:
            procurement?.proposalValidityPeriodMaxCounter,
        },
      }),
    }));
  };

  const cancelCounterEdit = () => setEditingCounterField(null);

  const putProcurementCounter = payload =>
    axios.put(
      `${api.API_URL}/procurement/update-procurement-counter/${procurementId}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

  const handleSaveCounter = async field => {
    const rawValue = String(counterDraft[field] ?? '').trim();
    if (!rawValue) {
      notify.warning('Please enter a counter value.');
      return;
    }
    if (!procurementId) {
      notify.error(
        'The procurement could not be identified. Return to the list and open it again.'
      );
      return;
    }

    let value;
    if (field === 'proposalValidityPeriodMax') {
      const parsedDate = new Date(rawValue);
      if (Number.isNaN(parsedDate.getTime())) {
        notify.warning(
          'Invalid date format. Choose a date from the date picker.'
        );
        return;
      }
      value = parsedDate.toISOString();
    } else {
      const parsedTolerance = parseOperationalToleranceValue(rawValue);
      if (parsedTolerance == null) {
        notify.warning(
          'Operational tolerance format is invalid. Enter a valid value and try again.'
        );
        return;
      }
      if (!String(counterDraft.operationalToleranceOption ?? '').trim()) {
        notify.warning('Please select an Operational Tolerance option.');
        return;
      }
      value = parsedTolerance;
    }

    const currentOt = isValidOperationalToleranceValue(
      procurement?.operationalToleranceCounter
    )
      ? parseOperationalToleranceValue(procurement.operationalToleranceCounter)
      : null;
    const currentValidity = isValidDateValue(
      procurement?.proposalValidityPeriodMaxCounter
    )
      ? new Date(procurement.proposalValidityPeriodMaxCounter).toISOString()
      : null;

    const payload = buildCounterPayload({
      field,
      value,
      operationalToleranceCounter:
        field === 'operationalTolerance' ? value : currentOt,
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? getOperationalToleranceOptionsCounter(
              counterDraft.operationalToleranceOption
            )
          : getOperationalToleranceOptionsCounter(
              resolveOperationalToleranceOption({
                label: procurement?.operationalToleranceOptionsCounterLabel,
                optionsCounter: procurement?.operationalToleranceOptionsCounter,
              })
            ),
      proposalValidityPeriodMaxCounter:
        field === 'proposalValidityPeriodMax' ? value : currentValidity,
    });
    if (!payload) return;

    setIsSavingCounter(true);
    try {
      await putProcurementCounter(payload);
      await refreshAfterCounterSave();
      setEditingCounterField(null);
      notify.success('Counter offer saved successfully');
    } catch (error) {
      notify.apiError(error, 'save the counter offer');
    } finally {
      setIsSavingCounter(false);
    }
  };

  const handleWithdrawCounter = async field => {
    if (!procurementId || !COUNTER_API_FIELD_MAP[field]) return;

    const confirmed = await confirmAction({
      tone: 'danger',
      title: 'Withdraw this counter offer?',
      message:
        'Your proposed counter value will be removed. You can submit a new counter offer afterwards.',
      subject:
        field === 'operationalTolerance'
          ? 'Operational tolerance'
          : 'Proposal validity period',
      confirmLabel: 'Withdraw counter offer',
    });
    if (!confirmed) return;

    const currentOt = isValidOperationalToleranceValue(
      procurement?.operationalToleranceCounter
    )
      ? parseOperationalToleranceValue(procurement.operationalToleranceCounter)
      : null;
    const currentValidity = isValidDateValue(
      procurement?.proposalValidityPeriodMaxCounter
    )
      ? new Date(procurement.proposalValidityPeriodMaxCounter).toISOString()
      : null;

    const payload = buildCounterPayload({
      field,
      value: null,
      operationalToleranceCounter:
        field === 'operationalTolerance' ? null : currentOt,
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? null
          : getOperationalToleranceOptionsCounter(
              resolveOperationalToleranceOption({
                label: procurement?.operationalToleranceOptionsCounterLabel,
                optionsCounter: procurement?.operationalToleranceOptionsCounter,
              })
            ),
      proposalValidityPeriodMaxCounter:
        field === 'proposalValidityPeriodMax' ? null : currentValidity,
    });
    if (!payload) return;

    setIsSavingCounter(true);
    try {
      await putProcurementCounter(payload);
      await refreshAfterCounterSave();
      setEditingCounterField(null);
      notify.success('Counter offer withdrawn successfully');
    } catch (error) {
      notify.apiError(error, 'withdraw the counter offer');
    } finally {
      setIsSavingCounter(false);
    }
  };

  const typeClauseResponses = useMemo(
    () => ({
      globalNotes: responses[TYPE_RESPONSE_GLOBAL] ?? [],
      productSpecification:
        responses[TYPE_RESPONSE_PRODUCT_SPECIFICATION] ?? [],
      counterPaymentTerm: responses[TYPE_RESPONSE_COUNTER_PAYMENT_TERM] ?? [],
      counterCompliance: responses[TYPE_RESPONSE_COUNTER_COMPLIANCE] ?? [],
      ...Object.fromEntries(
        THREAD_FIELDS.map(def => [def.key, responses[def.typeResponse] ?? []])
      ),
    }),
    [responses]
  );

  const [clausePendingIds, setClausePendingIds] = useState(null);
  const [clauseHasClarificationRespond, setClauseHasClarificationRespond] =
    useState(false);

  const handleClausePending = useCallback((ids, meta = {}) => {
    const nextIds = Array.isArray(ids) ? ids : [];
    setClausePendingIds(prev => {
      if (
        prev !== null &&
        prev.length === nextIds.length &&
        prev.every((id, index) => id === nextIds[index])
      ) {
        return prev;
      }
      return nextIds;
    });
    if (typeof meta.hasClarificationRespond === 'boolean') {
      setClauseHasClarificationRespond(prev =>
        prev === meta.hasClarificationRespond
          ? prev
          : meta.hasClarificationRespond
      );
    }
  }, []);

  const livePendingIds = useMemo(() => {
    const isPending = isEvaluatorView
      ? needsTeAgreeOnLastCounter
      : needsIcReplyOnEvaluatorThread;
    const ids = [];
    if (
      isPending(validityResponses) &&
      !(isEvaluatorView && resolvedGroups.proposalValidityPeriodMax)
    ) {
      ids.push('proposalValidityPeriodMax');
    }
    if (
      isPending(toleranceResponses) &&
      !(isEvaluatorView && resolvedGroups.operationalTolerance)
    ) {
      ids.push('operationalTolerance');
    }
    if (isPending(typeClauseResponses.globalNotes)) {
      ids.push('globalNotes');
    }
    if (isPending(typeClauseResponses.productSpecification)) {
      ids.push('productSpecification');
    }
    if (
      isPending(typeClauseResponses.counterPaymentTerm) &&
      !(isEvaluatorView && resolvedGroups.counterPaymentTerm)
    ) {
      ids.push('counterPaymentTerm');
    }
    if (
      isPending(typeClauseResponses.counterCompliance) &&
      !(isEvaluatorView && resolvedGroups.counterCompliance)
    ) {
      ids.push('counterCompliance');
    }
    THREAD_FIELDS.forEach(def => {
      if (
        isPending(typeClauseResponses[def.key]) &&
        !(isEvaluatorView && freeTextAgreed[def.key])
      ) {
        ids.push(def.key);
      }
    });
    return [...ids, ...(clausePendingIds ?? [])];
  }, [
    isEvaluatorView,
    validityResponses,
    toleranceResponses,
    typeClauseResponses,
    clausePendingIds,
    resolvedGroups.counterPaymentTerm,
    resolvedGroups.counterCompliance,
    resolvedGroups.proposalValidityPeriodMax,
    resolvedGroups.operationalTolerance,
    freeTextAgreed,
  ]);

  const hasClarificationRespond = useMemo(() => {
    if (!isEvaluatorView) return true;
    const lists = [
      validityResponses,
      toleranceResponses,
      typeClauseResponses.globalNotes,
      typeClauseResponses.productSpecification,
      typeClauseResponses.counterPaymentTerm,
      typeClauseResponses.counterCompliance,
      ...THREAD_FIELDS.map(def => typeClauseResponses[def.key]),
    ];
    return (
      lists.some(hasTeClarificationRespondReady) ||
      clauseHasClarificationRespond
    );
  }, [
    isEvaluatorView,
    validityResponses,
    toleranceResponses,
    typeClauseResponses,
    clauseHasClarificationRespond,
  ]);

  const highlightIds = useMemo(
    () => (highlightArmed ? livePendingIds : []),
    [highlightArmed, livePendingIds]
  );

  useEffect(() => {
    if (!onPendingRespondIds || isResponsesLoading || clausePendingIds === null)
      return;
    onPendingRespondIds(
      livePendingIds.map(id => ({
        id,
        tab: 'offering',
      })),
      { hasClarificationRespond }
    );
  }, [
    livePendingIds,
    onPendingRespondIds,
    isResponsesLoading,
    clausePendingIds,
    hasClarificationRespond,
  ]);

  const handleTypeDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const handleTypeSave = async clauseId => {
    const typeResponse = FIELD_TYPE_MAP[clauseId];
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!hasClauseResponse(respondNotes)) {
      notify.warning('Please enter a response note.');
      return;
    }
    try {
      const saved = await saveResponse(typeResponse, respondNotes);
      if (!saved) {
        notify.error('Failed to save response. Please try again.');
        return;
      }
      setDraftNotes(prev => ({
        ...prev,
        [clauseId]: saved?.respondNotes ?? respondNotes,
      }));
      setExpandedSections(prev => ({ ...prev, [clauseId]: true }));
      notify.success('Response saved successfully');
    } catch {
      notify.error('Failed to save response. Please try again.');
    }
  };

  const handleTypeSecondary = async (clauseId, hasSaved) => {
    if (hasSaved) {
      try {
        await deleteResponse(FIELD_TYPE_MAP[clauseId]);
        setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
        notify.success('Response removed successfully');
      } catch {
        notify.error('Failed to remove response. Please try again.');
      }
      return;
    }
    const own = getOwnClauseResponse(typeClauseResponses[clauseId], {
      isEvaluator: isEvaluatorView,
    });
    setDraftNotes(prev => ({ ...prev, [clauseId]: own?.respondNotes ?? '' }));
  };

  const renderTypeClauseCard = (
    key,
    title,
    description,
    {
      canInitiate = false,
      disableRespond = false,
      isRespondClosed: cardClosed = false,
      className = '',
      finalValuePrefill,
      allowLock = true,
    } = {}
  ) => (
    <Row className={className || undefined}>
      <ClauseCard
        showBidderConfirmation={false}
        clause={{
          id: key,
          clauseTitle: title,
          clauseDescription: description,
        }}
        clauseResponses={typeClauseResponses}
        draftNotes={draftNotes}
        expandedClauses={expandedSections}
        formMain={EVALUATOR_FORM_MAIN}
        isClauseResponsesSaving={isSaving}
        isClauseResponsesLoading={false}
        isEvaluatorView={isEvaluatorView}
        handleDraftChange={handleTypeDraftChange}
        handleClauseSecondaryAction={handleTypeSecondary}
        handleClauseSave={handleTypeSave}
        toggleClauseExpand={toggleSectionExpand}
        onLockResponse={
          isEvaluatorView && allowLock ? handleLockRespond : undefined
        }
        canInitiate={canInitiate}
        disableRespond={disableRespond}
        isRespondClosed={cardClosed}
        anchorId={`ic-respond-${key}`}
        highlightPending={highlightIds.includes(key)}
        finalValuePrefill={finalValuePrefill}
      />
    </Row>
  );

  const toggleSectionExpand = field => {
    setExpandedSections(prev => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;

      if (hasToleranceCounter && next.operationalTolerance === undefined) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (hasValidityCounter && next.proposalValidityPeriodMax === undefined) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }
      if (
        hasClauseResponse(toleranceOwnResponse?.respondNotes) &&
        !next.operationalTolerance
      ) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (
        hasClauseResponse(validityOwnResponse?.respondNotes) &&
        !next.proposalValidityPeriodMax
      ) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    hasToleranceCounter,
    hasValidityCounter,
    toleranceOwnResponse?.respondNotes,
    validityOwnResponse?.respondNotes,
  ]);

  useEffect(() => {
    if (!highlightIds.length) return;
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;
      highlightIds.forEach(id => {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [highlightIds]);

  useEffect(() => {
    setDraftNotes(prev => {
      const next = { ...prev };

      if (editingField !== 'operationalTolerance') {
        next.operationalTolerance = isEvaluatorView
          ? (toleranceOwnResponse?.respondNotes ?? '')
          : '';
      }
      if (editingField !== 'proposalValidityPeriodMax') {
        next.proposalValidityPeriodMax = isEvaluatorView
          ? (validityOwnResponse?.respondNotes ?? '')
          : '';
      }

      return next;
    });
  }, [
    editingField,
    isEvaluatorView,
    toleranceOwnResponse?.respondNotes,
    validityOwnResponse?.respondNotes,
  ]);

  const toleranceOption = resolveOperationalToleranceOption({
    label: procurement?.operationalToleranceOptionsCounterLabel,
    optionsCounter: procurement?.operationalToleranceOptionsCounter,
  });

  const validityLastEditable = getLastOwnEditableResponse(validityResponses, {
    isEvaluator: isEvaluatorView,
  });
  const toleranceLastEditable = getLastOwnEditableResponse(toleranceResponses, {
    isEvaluator: isEvaluatorView,
  });

  const startRespond = field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const lastEditable =
      typeResponse === TYPE_RESPONSE_TOLERANCE
        ? toleranceLastEditable
        : validityLastEditable;

    setDraftNotes(prev => ({
      ...prev,
      [field]: lastEditable?.respondNotes ?? '',
    }));
    setExpandedSections(prev => ({ ...prev, [field]: true }));
    setEditingField(field);
  };

  const cancelRespond = field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const own =
      typeResponse === TYPE_RESPONSE_TOLERANCE
        ? toleranceOwnResponse
        : validityOwnResponse;

    setDraftNotes(prev => ({
      ...prev,
      [field]: own?.respondNotes ?? '',
    }));
    setEditingField(null);
  };

  const handleSaveRespond = async field => {
    const typeResponse = FIELD_TYPE_MAP[field];
    const respondNotes = String(draftNotes[field] ?? '').trim();

    if (!hasClauseResponse(respondNotes)) {
      notify.warning('Please enter a response note.');
      return;
    }

    try {
      const saved = await saveResponse(typeResponse, respondNotes);
      if (!saved) {
        notify.error('Failed to save response. Please try again.');
        return;
      }
      setDraftNotes(prev => ({
        ...prev,
        [field]: saved?.respondNotes ?? respondNotes,
      }));
      setEditingField(null);
      setExpandedSections(prev => ({ ...prev, [field]: true }));
      notify.success('Response saved successfully');
    } catch {
      notify.error('Failed to save response. Please try again.');
    }
  };

  const handleDeleteRespond = async field => {
    const typeResponse = FIELD_TYPE_MAP[field];

    const subject =
      field === 'proposalValidityPeriodMax'
        ? 'Proposal validity response'
        : 'Operational tolerance response';
    if (!(await confirmDelete('response note', subject))) return;

    try {
      await deleteResponse(typeResponse);
      setDraftNotes(prev => ({ ...prev, [field]: '' }));
      setEditingField(null);
      notify.success('Response removed successfully');
    } catch {
      notify.error('Failed to remove response. Please try again.');
    }
  };

  const canRespondTolerance =
    canReplyTolerance && editingField !== 'operationalTolerance';

  const canRespondValidity =
    canReplyValidity && editingField !== 'proposalValidityPeriodMax';

  return (
    <>
      <SectionCard>
        <SectionHeader title='Product Specification/ Quantity' />
        <Row className='gy-4'>
          <Col lg={4}>
            <DetailItem
              label='Product Name'
              value={displayValue(productName)}
            />
          </Col>
          <Col lg={4}>
            <DetailItem
              label='Product Type'
              value={displayValue(productType)}
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Product Specification'
              value={productSpecification}
              isHtml
            />
          </Col>
        </Row>
        {renderTypeClauseCard(
          'productSpecification',
          'Product Specification Clarification',
          '',
          { canInitiate: false, isRespondClosed, className: 'mt-3' }
        )}
      </SectionCard>

      <Row className='mb-4'>
        <Col sm={12}>
          <IcRespondAnchor
            id='proposalValidityPeriodMax'
            pendingIds={highlightIds}
          >
            <CounterNegotiationCard
              title='Validity Period'
              subtitle='Review validity period requirements'
              notice={<DefaultAgreeNoticeAlert />}
              label='Valid To (Jakarta Local Time)'
              currentDisplay={formatDateTime(
                procurement?.proposalValidityPeriodMax
              )}
              extraFields={[
                {
                  label: 'Submission Deadline (Jakarta Local Time)',
                  value: formatProposalDeadline(
                    procurement?.proposalSubmissionDeadline
                  ),
                },
              ]}
              counterDisplay={
                hasValidityCounter
                  ? formatDateTime(
                      procurement?.proposalValidityPeriodMaxCounter
                    )
                  : null
              }
              hasCounter={hasValidityCounter}
              historyItems={validityHistory}
              isExpanded={
                hasValidityCounter ||
                Boolean(expandedSections.proposalValidityPeriodMax)
              }
              onToggleExpand={() =>
                toggleSectionExpand('proposalValidityPeriodMax')
              }
              editable={false}
              allowCounterEdit={allowValidityCounterEdit && !agreedValidity}
              allowThreadAgree={false}
              counterFooter={renderFieldAgreeRow([
                [
                  TYPE_RESPONSE_VALIDITY_VALID_TO,
                  'Valid To',
                  agreedValidity &&
                    (isValidDateValue(agreedValidity)
                      ? formatProposalDeadline(agreedValidity)
                      : agreedValidity),
                  otValidityAgreeable.validity,
                ],
              ])}
              isEditingCounter={
                editingCounterField === 'proposalValidityPeriodMax'
              }
              isEvaluatorView={isEvaluatorView}
              isEditing={editingField === 'proposalValidityPeriodMax'}
              isSaving={isSaving}
              isSavingCounter={isSavingCounter}
              isDraftDirty={
                isEvaluatorView
                  ? String(draftNotes.proposalValidityPeriodMax ?? '') !==
                    String(validityOwnResponse?.respondNotes ?? '')
                  : String(
                      draftNotes.proposalValidityPeriodMax ?? ''
                    ).trim() !== ''
              }
              isCounterDraftDirty={
                String(counterDraft.proposalValidityPeriodMax ?? '') !==
                String(procurement?.proposalValidityPeriodMaxCounter ?? '')
              }
              onRespond={
                canRespondValidity
                  ? () => startRespond('proposalValidityPeriodMax')
                  : undefined
              }
              onStartEdit={
                !isRespondClosed && validityLastEditable
                  ? () => startRespond('proposalValidityPeriodMax')
                  : undefined
              }
              onCancel={() => cancelRespond('proposalValidityPeriodMax')}
              onSave={() => handleSaveRespond('proposalValidityPeriodMax')}
              onStartCounterEdit={
                allowValidityCounterEdit
                  ? () => startCounterEdit('proposalValidityPeriodMax')
                  : undefined
              }
              onCancelCounterEdit={cancelCounterEdit}
              onSaveCounter={() =>
                handleSaveCounter('proposalValidityPeriodMax')
              }
              onDeleteCounter={
                allowValidityCounterEdit && hasValidityCounter
                  ? () => handleWithdrawCounter('proposalValidityPeriodMax')
                  : undefined
              }
              onDelete={
                !isRespondClosed && validityLastEditable
                  ? () => handleDeleteRespond('proposalValidityPeriodMax')
                  : undefined
              }
              isRespondClosed={isRespondClosed}
              proposeCounterLabel='Propose Counter Value Validity To (Jakarta Local Time)'
              counterEditor={
                <Flatpickr
                  className='form-control form-control-sm'
                  disabled={Boolean(agreedValidity)}
                  options={PROPOSAL_VALIDITY_FLATPICKR_OPTIONS}
                  value={
                    formatForFlatpickr(
                      counterDraft.proposalValidityPeriodMax
                    ) || ''
                  }
                  onChange={dates => {
                    setCounterDraft(prev => ({
                      ...prev,
                      proposalValidityPeriodMax: dates[0]?.toISOString() ?? '',
                    }));
                  }}
                />
              }
            >
              <RespondNotesEditor
                id='respond-proposal-validity'
                value={draftNotes.proposalValidityPeriodMax ?? ''}
                isDraftDirty={
                  isEvaluatorView
                    ? String(draftNotes.proposalValidityPeriodMax ?? '') !==
                      String(validityOwnResponse?.respondNotes ?? '')
                    : String(
                        draftNotes.proposalValidityPeriodMax ?? ''
                      ).trim() !== ''
                }
                onChange={e =>
                  setDraftNotes(prev => ({
                    ...prev,
                    proposalValidityPeriodMax: e.target.value,
                  }))
                }
              />
            </CounterNegotiationCard>
          </IcRespondAnchor>
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col sm={12}>
          <IcRespondAnchor id='operationalTolerance' pendingIds={highlightIds}>
            <CounterNegotiationCard
              title='Operational Tolerance'
              subtitle='Review operational tolerance requirements'
              notice={<DefaultAgreeNoticeAlert />}
              partyType={procurement?.partyType}
              label='Operational Tolerance'
              currentDisplay={formatOperationalTolerance(
                procurement?.operationalTolerance
              )}
              counterDisplay={
                isValidOperationalToleranceValue(
                  procurement?.operationalToleranceCounter
                )
                  ? formatOperationalTolerance(
                      procurement?.operationalToleranceCounter
                    )
                  : null
              }
              partyTypeCounter={toleranceOption || null}
              hasCounter={hasToleranceCounter}
              historyItems={toleranceHistory}
              isExpanded={
                hasToleranceCounter ||
                Boolean(expandedSections.operationalTolerance)
              }
              onToggleExpand={() => toggleSectionExpand('operationalTolerance')}
              editable={false}
              allowCounterEdit={
                allowToleranceCounterEdit && !(agreedOtValue && agreedOtOptions)
              }
              allowThreadAgree={false}
              counterFooter={renderFieldAgreeRow([
                [
                  TYPE_RESPONSE_OT_VALUE,
                  'Operational Tolerance',
                  agreedOtValue,
                  otValidityAgreeable.otValue,
                ],
                [
                  TYPE_RESPONSE_OT_OPTIONS,
                  'Operation Tolerance Options',
                  agreedOtOptions,
                  otValidityAgreeable.otOptions,
                ],
              ])}
              isEditingCounter={editingCounterField === 'operationalTolerance'}
              isEvaluatorView={isEvaluatorView}
              isEditing={editingField === 'operationalTolerance'}
              isSaving={isSaving}
              isSavingCounter={isSavingCounter}
              isDraftDirty={
                isEvaluatorView
                  ? String(draftNotes.operationalTolerance ?? '') !==
                    String(toleranceOwnResponse?.respondNotes ?? '')
                  : String(draftNotes.operationalTolerance ?? '').trim() !== ''
              }
              isCounterDraftDirty={
                String(counterDraft.operationalTolerance ?? '') !==
                  String(procurement?.operationalToleranceCounter ?? '') ||
                String(counterDraft.operationalToleranceOption ?? '') !==
                  String(toleranceOption ?? '')
              }
              onRespond={
                canRespondTolerance
                  ? () => startRespond('operationalTolerance')
                  : undefined
              }
              onStartEdit={
                !isRespondClosed && toleranceLastEditable
                  ? () => startRespond('operationalTolerance')
                  : undefined
              }
              onCancel={() => cancelRespond('operationalTolerance')}
              onSave={() => handleSaveRespond('operationalTolerance')}
              onStartCounterEdit={
                allowToleranceCounterEdit
                  ? () => startCounterEdit('operationalTolerance')
                  : undefined
              }
              onCancelCounterEdit={cancelCounterEdit}
              onSaveCounter={() => handleSaveCounter('operationalTolerance')}
              onDeleteCounter={
                allowToleranceCounterEdit && hasToleranceCounter
                  ? () => handleWithdrawCounter('operationalTolerance')
                  : undefined
              }
              onDelete={
                !isRespondClosed && toleranceLastEditable
                  ? () => handleDeleteRespond('operationalTolerance')
                  : undefined
              }
              isRespondClosed={isRespondClosed}
              proposeCounterLabel='Propose Counter Value Operation Tolerance (±)'
              counterEditor={
                <div className='d-flex flex-column gap-4 p-1'>
                  <div>
                    <Label
                      htmlFor='ic-customToleranceInput'
                      className='form-label text-muted small fw-semibold mb-2'
                    >
                      Counter Operation Tolerance (±)
                    </Label>
                    <div
                      className='input-group input-group-sm'
                      style={{ maxWidth: '240px' }}
                    >
                      <Input
                        id='ic-customToleranceInput'
                        type='number'
                        min='0'
                        step='0.01'
                        placeholder='0.00'
                        value={counterDraft.operationalTolerance ?? ''}
                        onChange={e =>
                          setCounterDraft(prev => ({
                            ...prev,
                            operationalTolerance: e.target.value,
                          }))
                        }
                        className='text-dark fw-medium'
                        disabled={Boolean(agreedOtValue)}
                      />
                      <span className='input-group-text bg-light text-muted fw-semibold px-3'>
                        %
                      </span>
                    </div>
                  </div>
                  <hr className='text-muted my-0 opacity-25' />
                  <div>
                    <Label className='form-label text-muted small fw-semibold mb-2'>
                      Counter Operation Tolerance Options
                    </Label>
                    <div className='d-flex flex-wrap gap-2'>
                      {OPERATIONAL_TOLERANCE_OPTIONS.map(option => {
                        const isChecked =
                          counterDraft.operationalToleranceOption ===
                          option.value;
                        return (
                          <div
                            key={option.value}
                            role='presentation'
                            className={`border rounded-3 px-3 py-2 mb-0 d-flex align-items-center gap-2 ${
                              isChecked
                                ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                                : 'border-light bg-light text-dark fw-medium'
                            }`}
                            style={{
                              cursor: agreedOtOptions
                                ? 'not-allowed'
                                : 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() =>
                              !agreedOtOptions &&
                              setCounterDraft(prev => ({
                                ...prev,
                                operationalToleranceOption: option.value,
                              }))
                            }
                          >
                            <Input
                              className='form-check-input ms-0 mt-0 flex-shrink-0'
                              type='radio'
                              id={`ic-ot-option-${option.value}`}
                              name='icOperationalToleranceOption'
                              value={option.value}
                              checked={isChecked}
                              disabled={Boolean(agreedOtOptions)}
                              onChange={e =>
                                setCounterDraft(prev => ({
                                  ...prev,
                                  operationalToleranceOption: e.target.value,
                                }))
                              }
                              style={{ cursor: 'pointer' }}
                            />
                            <Label
                              className='form-check-label fs-13 mb-0'
                              htmlFor={`ic-ot-option-${option.value}`}
                              style={{ cursor: 'pointer' }}
                            >
                              {option.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              }
            >
              <RespondNotesEditor
                id='respond-operational-tolerance'
                value={draftNotes.operationalTolerance ?? ''}
                isDraftDirty={
                  isEvaluatorView
                    ? String(draftNotes.operationalTolerance ?? '') !==
                      String(toleranceOwnResponse?.respondNotes ?? '')
                    : String(draftNotes.operationalTolerance ?? '').trim() !==
                      ''
                }
                onChange={e =>
                  setDraftNotes(prev => ({
                    ...prev,
                    operationalTolerance: e.target.value,
                  }))
                }
              />
            </CounterNegotiationCard>
          </IcRespondAnchor>
        </Col>
      </Row>

      <SectionCard>
        {renderTypeClauseCard(
          'globalNotes',
          'Notes',
          procurement?.globalNotes,
          { canInitiate: false, isRespondClosed }
        )}
        <hr className='my-4 border-secondary border-dashed' />
        <div className='mt-2'>
          <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
          <PortRestrictionsTable
            ports={procurement?.portRestriction}
            productId={getProcurementProductNameId(procurement)}
          />
        </div>
      </SectionCard>

      <ClauseDetailSection
        procurement={procurement}
        procurementId={procurementId}
        offerId={offerId}
        procurementMitraId={procurementMitraIdProp}
        isEvaluatorView={isEvaluatorView}
        isRespondClosed={isRespondClosed}
        highlightArmed={highlightArmed}
        onPendingRespondIds={handleClausePending}
      />

      <PaymentComplianceView
        procurement={procurement}
        procurementMitraId={bidder?.id}
        onRefreshProcurement={refreshAfterCounterSave}
        canCounterField={def =>
          !isEvaluatorView &&
          !isRespondClosed &&
          needsIcReplyOnEvaluatorThread(typeClauseResponses[def.key])
        }
        renderFieldRespond={def =>
          renderTypeClauseCard(def.key, `${def.label} Clarification`, '', {
            canInitiate:
              isEvaluatorView &&
              !resolvedGroups[def.key] &&
              isFieldCountered(def, latestCounterForField(def), procurement),
            // Sudah di-agree di field → Respond evaluator ditutup (#145).
            disableRespond: isEvaluatorView && resolvedGroups[def.key],
            isRespondClosed,
            className: 'mt-3',
            // Agree & Lock di field (19-21), bukan di bubble thread.
            allowLock: false,
          })
        }
        canCounterGroup={groupKey =>
          !isEvaluatorView &&
          !isRespondClosed &&
          needsIcReplyOnEvaluatorThread(typeClauseResponses[groupKey])
        }
        fieldAgreements={fieldAgreements}
        onAgreeField={
          isEvaluatorView && !isRespondClosed ? handleAgreeField : null
        }
        isAgreeSaving={isSaving}
        paymentRespond={renderTypeClauseCard(
          'counterPaymentTerm',
          'Invoicing Quantity, Mode & Currency Clarification',
          '',
          {
            canInitiate:
              isEvaluatorView &&
              hasPaymentChoiceCounter &&
              !resolvedGroups.counterPaymentTerm,
            disableRespond:
              isEvaluatorView && resolvedGroups.counterPaymentTerm,
            isRespondClosed,
            className: 'mt-3',
            // Agree per field saja; lock lama di bubble tampil read-only.
            allowLock: false,
          }
        )}
        complianceRespond={renderTypeClauseCard(
          'counterCompliance',
          'Law & Arbitration Clarification',
          '',
          {
            canInitiate:
              isEvaluatorView &&
              hasComplianceChoiceCounter &&
              !resolvedGroups.counterCompliance,
            disableRespond: isEvaluatorView && resolvedGroups.counterCompliance,
            isRespondClosed,
            className: 'mt-3',
            // Agree per field saja; lock lama di bubble tampil read-only.
            allowLock: false,
          }
        )}
      />
    </>
  );
};

export default OfferingTab;
